<template>
  <section v-show="supportOn" class="support-card">
    <!-- 회사 상세(부트스트랩 창) 안 별도 상자: 「수정하기」와 별개로 바로 적용(W6 A3, 계약 SUPPORT 9). 루트 앞 주석 금지($el) -->
    <div class="support-caption">
      고객 지원을 위한 접근
      <span class="support-instant">바로 적용됩니다 · 「수정하기」와 별개</span>
    </div>

    <!-- 상태 조회는 서버가 ops.support.edit·manage 만 받음(@PermStrict) — 권한 없는 운영자 틀이면 부르지 않음(10-30) -->
    <p v-if="!canSee" class="support-instant">지원 접근 권한(ops.support.edit)이 없어 볼 수 없습니다.</p>
    <div v-else-if="loadFailed" class="support-error" role="alert">
      <span>{{ loadFailed }}</span>
      <a-button size="small" :loading="loading" @click="load">다시 불러오기</a-button>
    </div>
    <a-spin v-else :spinning="loading">
      <dl class="support-summary">
        <!-- 리셀러 회사는 동의 없이 들어감(사용자 결정 10-29) -->
        <dt v-if="consentRequired">동의</dt>
        <dd v-if="consentRequired">
          <span :class="{ on: consent.on }">{{ consent.text }}</span>
          <!-- 누르면 바로 바뀌지 않고 사유 창부터(checked 는 서버 값만 따름) -->
          <a-switch
            v-if="buttons.consent"
            class="consent-switch"
            size="small"
            :checked="consent.on"
            :disabled="!status"
            :aria-label="consentTitle"
            @change="openConsent"
          />
          <div v-if="consent.by || consent.detail" class="sub-text">{{ [consent.by, consent.detail].filter(Boolean).join(" · ") }}</div>
          <div v-if="consent.offline" class="sub-text">
            {{ consent.offline }}
            <a-button v-if="buttons.evidence" size="small" type="link" @click="openEvidence">증빙 보기</a-button>
          </div>
        </dd>
        <dt>지원 계정</dt>
        <dd>
          서티라이프: {{ subAccountLine(subs.certilife, "CERTILIFE") }}
          <div v-if="subs.resellerLinked || subs.reseller" class="sub-text">
            리셀러: {{ subs.reseller ? `${subs.reseller.username || "리셀러 지원"} — ` : "" }}{{ subAccountLine(subs.reseller, "RESELLER") }} (보기만)
          </div>
        </dd>
      </dl>
      <div class="support-actions">
        <a-button v-if="buttons.enter" size="small" type="primary" class="yellow-button" @click="openEntry">지원 계정으로 접속</a-button>
        <!-- 버튼은 눌러도 됨 — 서버가 403 SUPPORT_CONSENT_REQUIRED 문구로 답함 -->
        <span v-if="buttons.enter && status && consentRequired && !consent.on" class="sub-text enter-hint">먼저 고객 지원을 위한 접근 허용을 켜 주세요</span>
        <a-button v-if="buttons.lock" size="small" danger @click="openDialog('lock')">잠금</a-button>
        <a-button v-if="buttons.unlock" size="small" @click="openDialog('unlock')">풀기</a-button>
        <a-button v-if="buttons.auditLog" size="small" type="link" @click="openAuditLog">이 회사 이용 내역</a-button>
      </div>
    </a-spin>
    <!-- 결과는 상자 안 줄에. 전역 알림은 이 부트스트랩 창 뒤에 가린다(H1) -->
    <p v-if="result" class="support-result" :class="result.type" role="status">{{ result.text }}</p>

    <!-- 「지원 계정으로 접속」(A4) — 목록·위쪽 검색과 같은 창(SupportEntryDialog) -->
    <support-entry-dialog
      v-model:open="entryOpen"
      :company-id="companyId"
      :company-type="companyType"
      :company-name="companyName"
      @opened="fetchStatus"
    />

    <!-- 동의 스위치(CONSENT-1028): 사유 → (본인 확인 기록이 없으면) 같은 창 안 본인 확인 → 같은 요청을 저절로 다시 -->
    <a-modal :open="dialog === 'consent'" :footer="null" :z-index="1990" :mask-closable="false" wrap-class-name="support-dialog" @cancel="closeDialog">
      <template #title>
        <div class="modal-title">{{ consentTitle }} {{ switchVerb }} — {{ companyName || "이 회사" }}</div>
      </template>
      <div class="modal-content">
        <template v-if="consentStep === 'form'">
          <label class="field-label">{{ switchVerb }} 사유(필수, 200자까지)</label>
          <a-textarea v-model:value="form.reason" :rows="2" :maxlength="200" placeholder="예: 유지보수 계약 — 장애 대응" />
          <p class="field-hint">
            {{
              form.status === "ON"
                ? "켜면 서티라이프·담당 리셀러가 이 회사에 지원 접속할 수 있습니다."
                : "끄면 지원 계정이 잠기고, 열린 지원 접속은 1분 안에 끝납니다."
            }}
          </p>
        </template>
        <template v-else>
          <p class="field-hint">{{ CONSENT_PROOF_HINT }}</p>
          <otp-step
            v-if="proofFactors.methods.length"
            :methods="proofFactors.methods"
            :targets="proofFactors.targets"
            :send="proofSend"
            :verify="proofVerify"
            :submit-text="`확인하고 ${switchVerb}`"
          />
        </template>
        <p v-if="dialogError" class="support-result error" role="alert">{{ dialogError }}</p>
        <div class="modal-footer">
          <a-button @click="closeDialog">취소</a-button>
          <a-button v-if="consentStep === 'form'" type="primary" class="yellow-button" :loading="busy" @click="submitConsent(false)">확인</a-button>
        </div>
      </div>
    </a-modal>

    <!-- 잠금 · 풀기(A5). 서면 동의 등록·동의 철회 기록은 화면에서 뺌(CONSENT-1028, 서버 API 는 남음) -->
    <a-modal
      :open="['lock', 'unlock'].includes(dialog)"
      :footer="null"
      :z-index="1990"
      :mask-closable="false"
      wrap-class-name="support-dialog"
      @cancel="closeDialog"
    >
      <template #title>
        <div class="modal-title">{{ DIALOG_TITLE[dialog] }}</div>
      </template>
      <div class="modal-content">
        <label class="field-label">사유(필수, 60자까지)</label>
        <a-textarea v-model:value="form.reason" :rows="2" :maxlength="60" />
        <p class="field-hint">
          {{ dialog === "lock" ? "잠그면 서티라이프 지원 계정으로 들어갈 수 없고, 열린 지원 접속은 1분 안에 끝납니다." : "풀면 동의가 켜진 동안 다시 지원 접속을 할 수 있습니다." }}
        </p>
        <p v-if="dialogError" class="support-result error" role="alert">{{ dialogError }}</p>
        <div class="modal-footer">
          <a-button @click="closeDialog">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="busy" @click="submitDialog">저장</a-button>
        </div>
      </div>
    </a-modal>
  </section>
</template>

<script>
import { errorMessage, factorsOf, getMfaToken, isStepUpCancelled, setMfaToken } from "@/utils/mfa";
import { canAny, permsOf } from "@/utils/perm";
import { standardErrorNotice } from "@/utils/standardErrors";
import {
  CONSENT_PROOF_HINT,
  PROOF_NEEDS_FACTOR_TEXT,
  PROOF_PURPOSE,
  cardButtons,
  consentDoneText,
  consentLine,
  consentSwitchBody,
  consentSwitchError,
  consentTitleOf,
  entryNextStep,
  lockBody,
  lockError,
  proofBody,
  proofErrorText,
  proofSkipped,
  safeEvidenceType,
  subAccountLine,
  subAccountsOf,
  supportAccessOn,
} from "@/utils/supportAccess";
import { SupportAdminApi, SupportProofApi, saveBlobResponse } from "@/api/standards";
import OtpStep from "./OtpStep.vue";
import SupportEntryDialog from "./SupportEntryDialog.vue";

const DIALOG_TITLE = { lock: "서티라이프 지원 계정 잠금", unlock: "서티라이프 지원 계정 풀기" };
/** 실패 문구: 본인 확인을 스스로 닫았으면 없음, 새 계약 코드는 공통 문구, 아니면 서버 문구 */
const failText = (error, fallback) => {
  if (isStepUpCancelled(error)) return "";
  const n = standardErrorNotice(error);
  return n ? n.text : errorMessage(error, fallback);
};

/**
 * 회사 상세 「고객 지원을 위한 접근」 카드(W6 A3~A5, CONSENT-1028 동의 스위치). 병원·제조사(기공소)·STUDIO 회사 정보 창에 「회사 전체 2차 인증 필수」 바로 아래.
 * 리셀러 회사 창에는 두지 않음(리셀러는 고객사가 아님). 버튼·문구 규칙은 utils/supportAccess.js.
 * 「발급」 버튼은 없다 — 첫 「지원 계정으로 접속」이 서브 계정 발급까지 한다(설계 3-3).
 */
export default {
  name: "SupportAccessCard",
  components: { OtpStep, SupportEntryDialog },
  props: {
    companyId: { type: String, default: "" },
    companyType: { type: String, default: "" },
    companyName: { type: String, default: "" },
  },
  data() {
    return {
      CONSENT_PROOF_HINT,
      DIALOG_TITLE,
      subAccountLine,
      status: null,
      loading: false,
      loadFailed: "",
      result: null,
      dialog: "",
      dialogError: "",
      busy: false,
      form: {},
      entryOpen: false,
      triedSaved: false,
      consentStep: "form",
      proofFactors: { methods: [], targets: {} },
    };
  },
  computed: {
    perms() {
      return permsOf(this.$store.state.introspect);
    },
    // supportAccess 가 꺼진 동안(배포 날)은 상자째 숨기고 부르지 않음 — 동의 스위치가 main 에 없던 것(10-31)
    supportOn() {
      return supportAccessOn(this.$store.state.introspect);
    },
    // introspect 를 받기 전엔 아직 모름 → 부르지 않음. perms 칸이 없으면(옛 inapi) 서버에 맡김
    canSee() {
      return this.supportOn && canAny(this.perms, ["ops.support.edit", "ops.support.manage"]);
    },
    buttons() {
      return cardButtons(this.status, this.perms);
    },
    consent() {
      return consentLine(this.status && this.status.consent);
    },
    consentTitle() {
      return consentTitleOf(this.status && this.status.consent);
    },
    switchVerb() {
      return this.form.status === "ON" ? "켜기" : "끄기";
    },
    subs() {
      return subAccountsOf(this.status);
    },
    consentRequired() {
      return !(this.status && this.status.consentRequired === false);
    },
  },
  watch: {
    companyId: { immediate: true, handler: "load" },
    canSee(v) {
      if (v) this.load();
    },
  },
  mounted() {
    this.modalEl = this.$el.closest && this.$el.closest(".modal");
    if (this.modalEl) this.modalEl.addEventListener("show.bs.modal", this.load);
    // 부트스트랩 창의 focus trap 이 이 카드의 창(ant 모달) 입력 칸 포커스를 빼앗지 않게(StepUpModal 과 같은 방식)
    window.addEventListener("focusin", this.keepFocus, true);
  },
  beforeUnmount() {
    if (this.modalEl) this.modalEl.removeEventListener("show.bs.modal", this.load);
    window.removeEventListener("focusin", this.keepFocus, true);
  },
  methods: {
    keepFocus(event) {
      if (this.dialog && event.target.closest && event.target.closest(".support-dialog")) event.stopPropagation();
    },
    // 회사가 바뀌거나 창이 다시 열릴 때: 결과 줄도 지움
    load() {
      this.status = null;
      this.result = null;
      return this.fetchStatus();
    },
    async fetchStatus() {
      const companyId = this.companyId;
      this.loadFailed = "";
      this.loading = !!companyId && this.canSee;
      if (!companyId || !this.canSee) return;
      try {
        const data = await SupportAdminApi.status(companyId);
        if (companyId === this.companyId) this.status = data;
      } catch (error) {
        console.error(error);
        if (companyId === this.companyId) this.loadFailed = failText(error, "고객 지원 접근 상태를 불러오지 못했습니다.") || "고객 지원 접근 상태를 불러오지 못했습니다.";
      } finally {
        if (companyId === this.companyId) this.loading = false;
      }
    },
    openDialog(name) {
      this.dialog = name;
      this.dialogError = "";
      this.form = { reason: "" };
    },
    closeDialog() {
      if (this.busy) return;
      this.dialog = "";
    },
    /** 스위치를 누르면: 사유 창(켜기·끄기). 값은 서버가 바꾼 뒤 다시 읽어서만 바뀜 */
    openConsent(checked) {
      if (!!checked === this.consent.on) return;
      this.openDialog("consent");
      this.triedSaved = false;
      this.form = { status: checked ? "ON" : "OFF", reason: "" };
      this.consentStep = "form";
    },
    /** PUT 동의 스위치. 본인 확인 기록이 없으면(403 SUPPORT_PROOF_REQUIRED) 같은 창에서 확인 뒤 한 번만 다시(proofDone) */
    async submitConsent(proofDone) {
      const problem = consentSwitchError(this.form);
      if (problem) {
        this.dialogError = problem;
        return;
      }
      this.busy = true;
      this.dialogError = "";
      try {
        await SupportAdminApi.setConsent(this.companyId, consentSwitchBody(this.form));
        this.result = { type: "success", text: consentDoneText(this.form.status) };
        this.busy = false;
        this.dialog = "";
        this.fetchStatus(); // 끄면 서브 계정도 잠김
      } catch (error) {
        console.error(error);
        const next = entryNextStep(error, proofDone);
        if (next && next.kind === "PROOF") await this.startProof(); // 수단을 읽는 동안 버튼은 바쁨
        else this.dialogError = next ? next.text : failText(error, "바꾸지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.busy = false;
      }
    },
    // 이 카드의 본인 확인은 동의 스위치 창만(접속은 SupportEntryDialog 가 따로 함)
    setProofStep(step) {
      this.consentStep = step;
    },
    setProofMessage(text) {
      this.dialogError = text;
    },
    openEntry() {
      this.entryOpen = true;
    },
    async startProof() {
      const mfa = await SupportProofApi.factors().catch((error) => {
        console.error(error);
        return null;
      });
      // 2차 인증을 쓰지 않는 사람은 본인 확인 없이 같은 요청을 한 번만 다시(T-CONTRACT-1031 §1). 또 막히면 entryNextStep 이 문구로
      if (proofSkipped(mfa)) {
        await this.submitConsent(true);
        return;
      }
      // 방금(10분 안) 받은 본인 확인 증표가 있으면 인증번호 없이 그대로(10-29) — 한 번만 시도
      const saved = getMfaToken();
      if (saved && !this.triedSaved) {
        this.triedSaved = true;
        try {
          await SupportProofApi.proof(proofBody(saved));
          await this.submitConsent(true);
          return;
        } catch (error) {
          console.error(error);
        }
      }
      this.setProofStep("proof");
      // 수단을 못 읽었으면 알림톡으로 시도(서버가 답함)
      this.proofFactors = mfa ? factorsOf(mfa) : { methods: ["ALIMTALK_OTP"], targets: {} };
      if (!this.proofFactors.methods.length) this.setProofMessage(PROOF_NEEDS_FACTOR_TEXT);
    },
    async proofSend(method) {
      const data = await SupportProofApi.challenge(PROOF_PURPOSE, method);
      // 2FA 예외면 인증번호 없이 증표가 오지만 /support/proof 가 거절한다(계약) → 그대로 보내 서버 답을 보인다
      if (data && data.mfaToken) await this.finishProof(data.mfaToken);
      return data;
    },
    async proofVerify(method, code) {
      const data = await SupportProofApi.verify(PROOF_PURPOSE, method, code);
      setMfaToken(data.mfaToken);
      await this.finishProof(data.mfaToken);
    },
    async finishProof(mfaToken) {
      try {
        await SupportProofApi.proof(proofBody(mfaToken));
      } catch (error) {
        console.error(error);
        // 이유별 문구(MFA_STALE·MFA_TOKEN_INVALID 는 인증번호를 다시 받아 확인 — 창은 그대로)
        this.setProofMessage(proofErrorText(error) || failText(error, "본인 확인을 마치지 못했습니다."));
        return;
      }
      this.setProofStep("form");
      // 사용자가 다시 누르지 않게 막혔던 같은 요청을 저절로 다시 보냄
      await this.submitConsent(true);
    },
    async submitDialog() {
      const name = this.dialog;
      const problem = lockError(this.form.reason);
      if (problem) {
        this.dialogError = problem;
        return;
      }
      this.busy = true;
      this.dialogError = "";
      try {
        await SupportAdminApi[name](this.companyId, lockBody(this.form.reason));
        this.result = { type: "success", text: name === "lock" ? "잠갔습니다. 열린 지원 접속은 1분 안에 끝납니다." : "풀었습니다." };
        this.busy = false;
        this.dialog = "";
        this.fetchStatus();
      } catch (error) {
        console.error(error);
        this.dialogError = failText(error, "저장하지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.busy = false;
      }
    },
    async openEvidence() {
      try {
        const res = await SupportAdminApi.evidence(this.companyId);
        // 같은 출처 blob 이라 실행될 수 있는 형식(html·svg)은 열지 않고 내려받기만(XSS 방지)
        const type = safeEvidenceType(res.data && res.data.type);
        if (!type) {
          saveBlobResponse(res, "evidence");
          return;
        }
        const url = URL.createObjectURL(new Blob([res.data], { type }));
        const win = window.open(url, "_blank");
        if (win) win.opener = null;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (error) {
        console.error(error);
        this.result = { type: "error", text: failText(error, "증빙을 열지 못했습니다.") || "증빙을 열지 못했습니다." };
      }
    },
    // 회사 상세 창(부트스트랩)을 닫지 않고 새 탭으로 그 회사의 이용 내역
    openAuditLog() {
      const href = this.$router.resolve({ name: "auditLogs", query: { companyId: this.companyId, companyName: this.companyName } }).href;
      const win = window.open(href, "_blank");
      if (win) win.opener = null;
    },
  },
};
</script>

<style scoped>
.support-card {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #e8ebed;
  border-radius: 8px;
  font-size: 13px;
}
.support-caption {
  font-weight: 600;
  color: #2f3337;
  margin-bottom: 8px;
}
.support-instant {
  margin-left: 6px;
  font-weight: 400;
  font-size: 12px;
  color: #5c6168;
}
.support-summary {
  display: grid;
  grid-template-columns: 70px 1fr;
  gap: 6px 12px;
  margin: 0 0 8px;
}
.support-summary dt {
  font-weight: 600;
  color: #5c6168;
}
.support-summary dd {
  margin: 0;
  word-break: keep-all;
}
.support-summary .on {
  color: #1b5e20;
}
.support-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.support-error {
  display: flex;
  gap: 8px;
  align-items: center;
  color: #c62828;
}
.support-result {
  margin: 8px 0 0;
  font-size: 12px;
}
.support-result.success {
  color: #1b5e20;
}
.support-result.error {
  color: #c62828;
}
.sub-text {
  font-size: 12px;
  color: #5c6168;
}
.field-label {
  display: block;
  margin: 12px 0 6px;
  font-size: 13px;
  font-weight: 600;
}
.field-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #5c6168;
}
.consent-switch {
  margin-left: 8px;
}
.enter-hint {
  align-self: center;
}
/* 창 본문 여백(레포 규칙 src/modal.mdc — 전역 .modal-content 테두리·.ant-modal-content padding 0 을 덮음, FIX-1101 12) */
.modal-content {
  padding: 20px;
  border: none;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.support-card :deep(.yellow-button),
.modal-footer :deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;
}
</style>
